import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { STATUS_LIST, STATUS_LABEL } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PlusCircle, X, LayoutGrid, Table2, Printer, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OrdersSearch } from "@/components/orders/OrdersSearch";
import { DateFilterBar } from "@/components/orders/DateFilterBar";
import { AuditSummary } from "@/components/orders/AuditSummary";
import { OrdersTable } from "@/components/orders/OrdersTable";
import { OrderRowCard, rowHref } from "@/components/orders/OrderRowCard";
import { ActivityView } from "@/components/orders/ActivityView";
import { OrderGroupCard, groupRowsByOrder } from "@/components/orders/OrderGroupCard";
import { FavoriteFilters, resolveFavorite } from "@/components/orders/FavoriteFilters";
import { presetRange, shortDay, DATE_FIELD_LABEL, fmtDateTime, PRESETS } from "@/lib/auditDates";
import { buildRowsHtml, buildActivityHtml, openPrintWindow, writeAndPrint } from "@/lib/auditPrint";

const PAGE = 300;
const VIEW_KEY = "orders_view_mode";

function useOrdersFilters() {
  const [params, setParams] = useSearchParams();
  const f = {
    q: params.get("q") || "",
    status: params.get("status") || "",
    branchId: params.get("branch_id") || "",
    techId: params.get("technician_id") || "",
    catId: params.get("category_id") || "",
    dfield: params.get("dfield") || "received",
    from: params.get("from") || "",
    to: params.get("to") || "",
    preset: params.get("preset") || "",
    sort: params.get("sort") || "",
    dir: params.get("dir") || "desc",
  };
  const KEYS = { q: "q", status: "status", branchId: "branch_id", techId: "technician_id", catId: "category_id", dfield: "dfield", from: "from", to: "to", preset: "preset", sort: "sort", dir: "dir" };
  function patch(obj, replace = false) {
    const next = new URLSearchParams(window.location.search);
    Object.entries(obj).forEach(([k, v]) => (v ? next.set(KEYS[k], v) : next.delete(KEYS[k])));
    setParams(next, { replace });
  }
  return {
    f, patch, current: Object.fromEntries(params),
    reset: () => setParams(new URLSearchParams()),
    setAll: (obj) => setParams(new URLSearchParams(Object.entries(obj).filter(([, v]) => v))),
  };
}

function apiParams(f) {
  const p = {};
  if (f.q) p.q = f.q;
  if (f.status) p.status_filter = f.status;
  if (f.branchId) p.branch_id = f.branchId;
  if (f.techId) p.technician_id = f.techId;
  if (f.catId) p.category_id = f.catId;
  if (f.from) p.date_from = f.from;
  if (f.to) p.date_to = f.to;
  if (f.dfield !== "activity" && (f.from || f.to)) p.date_field = f.dfield;
  if (f.sort) p.sort = f.sort;
  if (f.dir) p.dir = f.dir;
  return p;
}

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { f, patch, reset, current, setAll } = useOrdersFilters();
  const [data, setData] = useState({ rows: [], events: [], total: 0, summary: null });
  const [branches, setBranches] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState("");
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "cards");
  const isActivity = f.dfield === "activity";

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/categories"),
    ]).then(([b, u, c]) => {
      setBranches(b.data.filter((x) => x.active));
      setTechnicians((u.data || []).filter((x) => x.role === "technician" && x.active));
      setCategories(c.data.filter((x) => x.active));
    });
  }, []);

  const paramsKey = JSON.stringify(apiParams(f));
  useEffect(() => {
    if (isActivity && !f.from && !f.to) {
      const [a, b] = presetRange("today");
      patch({ preset: "today", from: a, to: b }, true);
      return;
    }
    setLoading(true);
    const p = apiParams(f);
    const req = isActivity
      ? api.get("/orders/activity", { params: p })
      : api.get("/orders/audit", { params: { ...p, limit: showAll ? 0 : PAGE } });
    req
      .then((r) => setData({ rows: r.data.rows || [], events: r.data.events || [], total: r.data.total, summary: r.data.summary }))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, showAll, isActivity]);

  function changeView(v) { setView(v); localStorage.setItem(VIEW_KEY, v); }
  function applyFavorite(fav) {
    setShowAll(false);
    setAll(resolveFavorite(fav.filters));
    if (fav.filters.view) changeView(fav.filters.view);
  }
  function onSort(key) { patch({ sort: key, dir: f.sort === key && f.dir === "desc" ? "asc" : "desc" }); }

  const techName = technicians.find((t) => t.id === f.techId)?.name || (f.techId ? "Seleccionado" : "Todos");
  const branchName = branches.find((b) => b.id === f.branchId)?.name || "Todas";
  const periodLabel = f.from || f.to ? `${shortDay(f.from) || "inicio"} – ${shortDay(f.to) || "hoy"}` : "Todo el historial";
  const hasFilters = f.q || f.status || f.branchId || (f.techId && user.role !== "technician") || f.catId || f.from || f.to || f.dfield !== "received";

  const presetLabel = PRESETS.find((p) => p.key === f.preset && p.key !== "custom")?.label;
  const suggestedName = [
    f.techId && techName,
    f.branchId && branchName,
    f.status && STATUS_LABEL[f.status],
    f.dfield !== "received" && DATE_FIELD_LABEL[f.dfield],
    presetLabel || ((f.from || f.to) && periodLabel),
    f.q && `"${f.q}"`,
  ].filter(Boolean).join(" · ");

  const printMeta = useMemo(() => ({
    Generado: fmtDateTime(new Date().toISOString()),
    "Consultar por": DATE_FIELD_LABEL[f.dfield],
    Período: periodLabel,
    Técnico: techName,
    Sucursal: branchName,
    Estado: f.status ? STATUS_LABEL[f.status] : "Todos",
    ...(f.q ? { Búsqueda: f.q } : {}),
  }), [f.dfield, periodLabel, techName, branchName, f.status, f.q]);

  async function handlePrint() {
    const w = openPrintWindow();
    if (!w) return toast.error("Permití las ventanas emergentes para imprimir");
    setBusy("print");
    try {
      const p = apiParams(f);
      if (isActivity) {
        const r = await api.get("/orders/activity", { params: p });
        writeAndPrint(w, buildActivityHtml(r.data.events, printMeta, r.data.summary));
      } else {
        const r = await api.get("/orders/audit", { params: { ...p, limit: 0 } });
        const groupBy = f.from || f.to ? f.dfield : null;
        writeAndPrint(w, buildRowsHtml(r.data.rows, printMeta, r.data.summary, groupBy));
      }
    } catch (e) {
      w.close();
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  async function handleExcel() {
    setBusy("excel");
    try {
      const r = await api.get("/orders/audit/export", {
        params: { ...apiParams(f), mode: isActivity ? "activity" : "rows" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ordenes_${isActivity ? "actividad" : f.dfield}_${f.from || "todo"}_${f.to || "todo"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo generar el Excel");
    } finally {
      setBusy("");
    }
  }

  const list = isActivity ? data.events : data.rows;
  const groups = useMemo(() => groupRowsByOrder(data.rows), [data.rows]);

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Órdenes de Reparación</h1>
          <p className="text-slate-500 text-sm mt-1">Operación diaria y auditoría del taller</p>
        </div>
        <Link to="/orders/new" className="btn-primary" data-testid="orders-new-button">
          <PlusCircle size={18} /> Nueva Orden
        </Link>
      </div>

      <div className="card-base p-4 space-y-3">
        <FavoriteFilters current={current} view={view} suggestedName={suggestedName} onApply={applyFavorite} />
        <div className="flex items-center gap-2">
          <OrdersSearch q={f.q} onApply={(v) => patch({ q: v }, true)} />
          {hasFilters && (
            <button onClick={() => { reset(); setShowAll(false); }} className="btn-secondary" data-testid="orders-clear-filters">
              <X size={16} /> Limpiar
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <select value={f.status} onChange={(e) => patch({ status: e.target.value })} className="input-base py-2.5 text-sm" data-testid="filter-status">
            <option value="">Estado actual — Todos</option>
            {STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <select value={f.branchId} onChange={(e) => patch({ branchId: e.target.value })} className="input-base py-2.5 text-sm" data-testid="filter-branch">
            <option value="">Sucursal — Todas</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={f.catId} onChange={(e) => patch({ catId: e.target.value })} className="input-base py-2.5 text-sm" data-testid="filter-category">
            <option value="">Categoría — Todas</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {user.role !== "technician" && (
            <select value={f.techId} onChange={(e) => patch({ techId: e.target.value })} className="input-base py-2.5 text-sm" data-testid="filter-technician">
              <option value="">Técnico — Todos</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </div>
        <div className="pt-3 border-t border-slate-200">
          <DateFilterBar dfield={f.dfield} from={f.from} to={f.to} preset={f.preset} onChange={(o) => patch(o)} />
        </div>
      </div>

      <AuditSummary summary={data.summary} periodLabel={periodLabel} techLabel={techName} activity={isActivity} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-500" data-testid="orders-result-count">
          {loading ? "Cargando…" : isActivity
            ? `${data.total} acontecimientos`
            : `${data.total} registros${!showAll && data.total > data.rows.length ? ` · mostrando ${data.rows.length}` : ""}`}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isActivity && (
            <div className="inline-flex rounded-full border border-slate-300 bg-white p-0.5" data-testid="orders-view-toggle">
              {[["cards", "Tarjetas", LayoutGrid], ["table", "Tabla", Table2]].map(([k, label, Icon]) => (
                <button key={k} type="button" onClick={() => changeView(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid={`orders-view-${k}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={handlePrint} disabled={!!busy} className="btn-secondary !py-2 text-sm" data-testid="orders-print-btn">
            {busy === "print" ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />} Imprimir listado
          </button>
          <button type="button" onClick={handleExcel} disabled={!!busy} className="btn-secondary !py-2 text-sm" data-testid="orders-excel-btn">
            {busy === "excel" ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Descargar Excel (.xlsx)
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono">Cargando órdenes…</div>
      ) : isActivity ? (
        <ActivityView events={data.events} />
      ) : list.length === 0 ? (
        <div className="card-base p-10 text-center"><div className="text-slate-500">Sin órdenes que coincidan con los filtros.</div></div>
      ) : view === "table" ? (
        <OrdersTable rows={groups.flat()} sort={f.sort || (f.from || f.to ? f.dfield : "received")} dir={f.dir} onSort={onSort} onOpen={(r) => navigate(rowHref(r))} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="orders-cards">
          {groups.map((g) => (g[0].device_count > 1
            ? <OrderGroupCard key={g[0].order_id} rows={g} />
            : <OrderRowCard key={g[0].row_id} r={g[0]} />))}
        </div>
      )}

      {!loading && !isActivity && !showAll && data.total > data.rows.length && (
        <div className="text-center">
          <button type="button" onClick={() => setShowAll(true)} className="btn-secondary" data-testid="orders-show-all-btn">
            Ver los {data.total} registros
          </button>
        </div>
      )}
    </div>
  );
}
